"use client";

import { IResource, MapCompositeType, MapIcon } from "@gettactic/api";
import React from "react";
import { ConstructionAnimationProvider } from "./components/map/animation/ConstructionAnimationContext";
import {
  MapCanvasProvider,
  MapCanvasValue
} from "./components/map/contexts/MapCanvasContext";
import type {
  MapSaveMode,
  MapSaveOptions,
  MapSaveResult
} from "./components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { MapCanvasState } from "./components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import {
  MapEditorProvider,
  MapEditorState
} from "./components/map/contexts/MapEditorContext";
import type { ZoneEditorProps } from "./components/map/contexts/MapEditorContext/MapEditorContext.types";
import { MapEditor } from "./components/map/editor/MapEditor";
import { EditorShortcutProvider } from "./components/map/editor/shortcuts/EditorShortcutContext/EditorShortcutContext";
import { shortcuts } from "./components/map/editor/shortcuts/shortcuts";
import { MapEditorTool } from "./components/map/types";
import {
  MapCurrentPosition,
  MapInteractiveZones,
  ResourceEditingOptions,
  ZoneTypeDefinition
} from "./types";

export interface MapBuilderProps {
  onSave: (
    data: MapCanvasValue,
    saveOptions: MapSaveOptions
  ) => MapSaveResult | Promise<MapSaveResult>;
  value?: MapCanvasValue;
  resources?: IResource[];
  /** Per-resource live state (e.g. walk-up) rendered on the editing canvas —
   *  same record shape the read-only viewer consumes. */
  interactiveZones?: MapInteractiveZones;
  resourceId?: string;
  initialPosition?: MapCurrentPosition;
  setEditingResources: (value: ResourceEditingOptions) => void;
  editingResources?: ResourceEditingOptions;
  editorRef?: React.RefObject<MapEditorState | null | undefined>;
  teamColors?: Record<string, string>;
  fetchMapVersions?: MapCanvasState["fetchMapVersions"];
  activateMapVersion?: MapCanvasState["activateMapVersion"];
  onUploadIcon?: MapCanvasState["onUploadIcon"];
  canRebuildWithAi?: boolean;
  mapIcons?: MapIcon[];
  apiCompositeTypes?: MapCompositeType[];
  saveCompositeType?: MapCanvasState["saveCompositeType"];
  updateCompositeType?: MapCanvasState["updateCompositeType"];
  deleteCompositeType?: MapCanvasState["deleteCompositeType"];
  onExit?: () => void;
  /** Display name of the map being edited (e.g. the floor name). */
  title?: string;
  /** Muted context shown before the title (e.g. the office name). */
  titlePrefix?: string;
  /** Custom node rendered in place of the toolbar title (e.g. office /
   *  floor switchers). Renders inside the editor providers, so it can use
   *  the editor context hooks. */
  headerSlot?: React.ReactNode;
  /** The host's zone form for the Zones panel — see {@link ZoneEditorProps}. */
  zoneEditor?: React.ComponentType<ZoneEditorProps>;
  /** Trims the toolbars down to selection, pan, the resource creator/style
   *  tools, and snap-to-grid — see {@link MapEditorPropsState.restrictedMode}. */
  restrictedMode?: boolean;
  /** Save modes the Save dialog offers, in order; all three by default. */
  saveModes?: MapSaveMode[];
  /** Tool the canvas starts on, instead of the default "select". */
  initialActiveTool?: MapEditorTool;
  /** Start on the zone tool, tagging what is drawn with this zone. */
  initialZoneToolTag?: ZoneTypeDefinition | null;
  /** Start with zones shown on the canvas, instead of only once a zone
   *  tool is picked. */
  initialShowZones?: boolean;
  /** A read-only reference point rendered on the canvas (editing-zone/shape
   *  coordinates) — e.g. where a workplace request was originally pinned,
   *  so an approver placing a desk knows where to put it. */
  guidePoint?: { x: number; y: number };
}

export const MapBuilder: React.FC<MapBuilderProps> = (props) => {
  const {
    value,
    resources,
    interactiveZones,
    resourceId,
    initialPosition,
    setEditingResources,
    editingResources,
    onSave,
    editorRef,
    teamColors,
    fetchMapVersions,
    activateMapVersion,
    onUploadIcon,
    canRebuildWithAi,
    mapIcons,
    apiCompositeTypes,
    saveCompositeType,
    updateCompositeType,
    deleteCompositeType,
    onExit,
    title,
    titlePrefix,
    headerSlot,
    zoneEditor,
    restrictedMode,
    saveModes,
    initialActiveTool,
    initialZoneToolTag,
    initialShowZones,
    guidePoint
  } = props;

  const [fontLoaded, setFontLoaded] = React.useState(false);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Quicksand:wght@300;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    document.fonts.load("16px Quicksand").then(() => {
      setFontLoaded(true);
    });
  }, []);

  if (!fontLoaded) return;

  return (
    <ConstructionAnimationProvider>
      <MapCanvasProvider
        value={value}
        initialPosition={initialPosition}
        interactiveZones={interactiveZones}
        teamColors={teamColors}
        resourceId={resourceId}
        fetchMapVersions={fetchMapVersions}
        activateMapVersion={activateMapVersion}
        onUploadIcon={onUploadIcon}
        canRebuildWithAi={canRebuildWithAi}
        mapIcons={mapIcons}
        apiCompositeTypes={apiCompositeTypes}
        saveCompositeType={saveCompositeType}
        updateCompositeType={updateCompositeType}
        deleteCompositeType={deleteCompositeType}
      >
        <MapEditorProvider
          resources={resources}
          setEditingResources={setEditingResources}
          editingResources={editingResources}
          editorRef={editorRef}
          onSave={onSave}
          onExit={onExit}
          title={title}
          titlePrefix={titlePrefix}
          headerSlot={headerSlot}
          zoneEditor={zoneEditor}
          restrictedMode={restrictedMode}
          saveModes={saveModes}
          initialActiveTool={initialActiveTool}
          initialZoneToolTag={initialZoneToolTag}
          initialShowZones={initialShowZones}
          guidePoint={guidePoint}
        >
          <EditorShortcutProvider defaultShortcuts={shortcuts}>
            <MapEditor />
          </EditorShortcutProvider>
        </MapEditorProvider>
      </MapCanvasProvider>
    </ConstructionAnimationProvider>
  );
};
